"use client";

/**
 * P9-14 · Drag-to-dismiss for a bottom sheet (P9-DB02). Pointer events drive a
 * `translateY` only — no layout-affecting property changes — so the sheet stays
 * on the compositor and the drag holds 60fps on a mid-range phone.
 *
 * Release resolves by distance OR velocity, which is what makes it feel native:
 * a slow long drag past the threshold dismisses, and so does a short sharp
 * flick that never travelled far.
 */
import * as React from "react";

/** Past this many pixels a release dismisses regardless of speed. */
export const DISMISS_DISTANCE_PX = 120;
/** …or this fast (px/ms ≈ 500px/s), so a flick doesn't have to travel far. */
export const DISMISS_VELOCITY_PX_PER_MS = 0.5;

/** The release decision — pure, so the feel is locked down by tests. */
export function shouldDismissSheet(gesture: {
  distance: number;
  velocity: number;
}): boolean {
  // Upward drags (negative distance) never dismiss.
  if (gesture.distance <= 0) return false;
  return (
    gesture.distance >= DISMISS_DISTANCE_PX ||
    gesture.velocity >= DISMISS_VELOCITY_PX_PER_MS
  );
}

export type SheetDrag = {
  /** Current downward displacement in px (0 when idle). */
  offset: number;
  /** True while a finger is down — callers drop the transition to track 1:1. */
  dragging: boolean;
  /** Spread onto the drag handle. */
  handleProps: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
  };
};

export function useSheetDrag({
  onDismiss,
  disabled = false,
}: {
  onDismiss: () => void;
  disabled?: boolean;
}): SheetDrag {
  const [offset, setOffset] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const start = React.useRef<{ y: number; t: number } | null>(null);

  const end = React.useCallback(
    (clientY: number, dismissAllowed: boolean) => {
      const from = start.current;
      start.current = null;
      setDragging(false);
      setOffset(0);
      if (!from || !dismissAllowed) return;
      const distance = clientY - from.y;
      const elapsed = Math.max(performance.now() - from.t, 1);
      if (shouldDismissSheet({ distance, velocity: distance / elapsed })) {
        onDismiss();
      }
    },
    [onDismiss],
  );

  return {
    offset,
    dragging,
    handleProps: {
      onPointerDown: (e) => {
        if (disabled) return;
        start.current = { y: e.clientY, t: performance.now() };
        setDragging(true);
        // Capture so a fast drag that leaves the handle still reports moves.
        // Throws InvalidPointerId if the pointer is already gone — the drag
        // still works uncaptured, so never let it break the gesture.
        try {
          e.currentTarget.setPointerCapture?.(e.pointerId);
        } catch {
          /* uncaptured drag is fine */
        }
      },
      onPointerMove: (e) => {
        if (!start.current) return;
        // Clamp at 0: the sheet is already at its top stop, so an upward drag
        // must not lift it off the bottom edge.
        setOffset(Math.max(0, e.clientY - start.current.y));
      },
      onPointerUp: (e) => end(e.clientY, true),
      // A cancelled pointer (scroll takeover, call interrupt) snaps back.
      onPointerCancel: (e) => end(e.clientY, false),
    },
  };
}
